// GIS service — permitted-layer enforcement is server-side via geo-api.
import { geoApi } from '@/lib/geoApi';

/** Geological data classification taxonomy (matches geo-api enum). */
export type GeoClassification =
  | 'public'
  | 'restricted'
  | 'confidential'
  | 'executive_only'
  | 'ministry_restricted'
  | 'investor_limited'
  | 'NDA_required'
  | 'MOU_required';

export interface GisLayer {
  id: string;
  name: string;
  category: string;
  permitted: boolean;
  restricted?: boolean;
  requiresNda?: boolean;
  requiresMou?: boolean;
  classification?: GeoClassification;
  description?: string;
  // Future-ready spatial fields (populated by geo-api once published).
  geometryType?: 'Point' | 'LineString' | 'Polygon' | 'MultiPolygon' | string;
  geojsonSource?: string | null;
  bbox?: [number, number, number, number];
  accessLevel?: string;
  visible?: boolean;
  hasGeometry?: boolean;
}

export interface GeoJsonFeature {
  type: "Feature";
  geometry: { type: string; coordinates: unknown } | null;
  properties: Record<string, unknown> | null;
  id?: string | number;
}

export interface GeoJsonFeatureCollection {
  type: "FeatureCollection";
  features: GeoJsonFeature[];
}

export interface GeoDataset {
  id: string;
  title: string;
  category: string;
  classification: GeoClassification;
  region?: string;
  updatedAt?: string;
  thumbnailUrl?: string;
  summary?: string;
  /** Publication-readiness lifecycle, when geo-api reports it. */
  publicationStatus?: PublicationStatus;
}


export interface DisclosureRequestStatus {
  id: string;
  datasetId: string;
  datasetTitle?: string;
  state: 'submitted' | 'in-review' | 'approved' | 'rejected' | 'expired';
  submittedAt: string;
  decidedAt?: string;
  reviewer?: string;
}

export interface SpatialQueryPayload {
  layerId: string;
  bbox?: [number, number, number, number];
  attributes?: Record<string, string | number>;
}

export interface ExportRequestPayload {
  layerId: string;
  format: 'geojson' | 'shp' | 'csv';
  bbox?: [number, number, number, number];
}


/* ─────────── GIS Data Integration & PostGIS Readiness ─────────── */

/** Dataset publication-readiness lifecycle (matches geo-api enum). */
export type PublicationStatus =
  | 'pending_validation'
  | 'under_review'
  | 'approved'
  | 'published'
  | 'restricted'
  | 'archived';

export interface ReadinessDimension {
  key: string;
  label: string;
  /** 0–100 completeness, when geo-api reports it. */
  score?: number;
  status?: 'not_started' | 'in_progress' | 'ready' | 'pending';
  note?: string;
}

export interface GisReadinessOverview {
  dimensions: ReadinessDimension[];
  updatedAt?: string;
}

/** Public-safe default readiness dimensions (no invented data/metrics). */
export const READINESS_DIMENSIONS: ReadinessDimension[] = [
  { key: 'data', label: 'Data Availability', status: 'pending', note: 'Awaiting validated spatial data.' },
  { key: 'metadata', label: 'Metadata Readiness', status: 'pending', note: 'Metadata completeness under review.' },
  { key: 'publication', label: 'Publication Readiness', status: 'pending', note: 'Pending publication approval.' },
  { key: 'field', label: 'Field Readiness', status: 'pending', note: 'Field-capture configuration in preparation.' },
  { key: 'integration', label: 'Integration Readiness', status: 'pending', note: 'GeoJSON / PostGIS contracts staged.' },
];

/** Future layer categories — readiness placeholders only, no live geometry. */
export const GIS_LAYER_CATEGORIES: { key: string; label: string }[] = [
  { key: 'geological_units', label: 'Geological Units' },
  { key: 'concession_boundaries', label: 'Concession Boundaries' },
  { key: 'mineral_occurrences', label: 'Mineral Occurrences' },
  { key: 'environmental_zones', label: 'Environmental Zones' },
  { key: 'restricted_areas', label: 'Restricted Areas' },
  { key: 'infrastructure_layers', label: 'Infrastructure Layers' },
];

/** Future GeoJSON/PostGIS-ready contract fields (presented as readiness). */
export const GEOJSON_CONTRACT_FIELDS: { key: string; label: string }[] = [
  { key: 'layerId', label: 'Layer Identifier' },
  { key: 'layerName', label: 'Layer Name' },
  { key: 'classification', label: 'Classification' },
  { key: 'geometryType', label: 'Geometry Type' },
  { key: 'bbox', label: 'Bounding Box' },
  { key: 'geojsonSource', label: 'GeoJSON Source' },
  { key: 'publicationStatus', label: 'Publication Status' },
  { key: 'accessLevel', label: 'Access Level' },
];

/** Import & data-quality readiness checklist (public-safe, executive-friendly). */
export const IMPORT_QUALITY_READINESS: { key: string; label: string }[] = [
  { key: 'geojson', label: 'GeoJSON Readiness' },
  { key: 'shapefile', label: 'Shapefile Metadata Readiness' },
  { key: 'kml', label: 'KML / KMZ Metadata Readiness' },
  { key: 'csv', label: 'CSV Coordinate Readiness' },
  { key: 'geometry_validation', label: 'Geometry Validation' },
  { key: 'metadata_completeness', label: 'Metadata Completeness' },
  { key: 'classification_review', label: 'Classification Review' },
  { key: 'publication_approval', label: 'Publication Approval' },
];


// WP2.2 I-03: sample GIS fallback data removed. Layers/datasets are returned
// ONLY from geo-api; when the API returns nothing, callers receive an empty
// array and render an approved governed Empty/Unavailable state — never sample
// records presented as official data. (DESIGNS DEFINE PRESENTATION · APIS DEFINE TRUTH.)

export const gisService = {
  async listAllLayers(): Promise<GisLayer[]> {
    const res = await geoApi.get<GisLayer[]>('/gis/layers/all');
    return Array.isArray(res.data) ? res.data : [];
  },
  async listPermittedLayers(): Promise<GisLayer[]> {
    const res = await geoApi.get<GisLayer[]>('/gis/layers');
    const layers = Array.isArray(res.data) ? res.data : [];
    return layers.filter((l) => l.permitted);
  },
  /** Restricted layers shown as locked until geo-api grants access. */
  async listRestrictedLayers(): Promise<GisLayer[]> {
    const res = await geoApi.get<GisLayer[]>('/gis/layers/all');
    const layers = Array.isArray(res.data) ? res.data : [];
    return layers.filter((l) => !l.permitted);
  },
  getLayerFeatures(layerId: string) {
    return geoApi.get<GeoJsonFeatureCollection>(`/gis/layers/${encodeURIComponent(layerId)}/features`);
  },

  async listDatasets(): Promise<GeoDataset[]> {
    const res = await geoApi.get<GeoDataset[]>('/gis/datasets');
    return Array.isArray(res.data) ? res.data : [];
  },
  listDisclosureRequests() {
    return geoApi.get<DisclosureRequestStatus[]>('/gis/disclosure-requests');
  },
  runQuery(payload: SpatialQueryPayload) {
    return geoApi.post<{ features: unknown[] }>('/gis/query', payload);
  },
  requestExport(payload: ExportRequestPayload) {
    return geoApi.post<{ jobId: string }>('/gis/exports', payload);
  },
  listExports() {
    return geoApi.get<{ id: string; status: string; createdAt: string }[]>('/gis/exports');
  },

  /**
   * GIS readiness overview. Consumes the admin readiness endpoint when the
   * caller is authorized; on 401/403 (or any failure) returns an unauthorized
   * marker so the UI can render a public-safe explanation instead of an error.
   */
  async getReadinessOverview(): Promise<{
    authorized: boolean;
    overview: GisReadinessOverview | null;
  }> {
    const res = await geoApi.get<GisReadinessOverview>('/admin/gis-readiness/overview');
    if (res.status === 401 || res.status === 403) {
      return { authorized: false, overview: null };
    }
    if (res.error || !res.data) {
      return { authorized: false, overview: null };
    }
    return { authorized: true, overview: res.data };
  },

  /** Executive readiness summary — authorized callers only; degrades safely. */
  async getReadinessExecutive(): Promise<{
    authorized: boolean;
    overview: GisReadinessOverview | null;
  }> {
    const res = await geoApi.get<GisReadinessOverview>('/admin/gis-readiness/executive');
    if (res.status === 401 || res.status === 403 || res.error || !res.data) {
      return { authorized: false, overview: null };
    }
    return { authorized: true, overview: res.data };
  },
};

